import React from 'react';
import { faculty } from '../data/mock';
import { Award, BookOpen, GraduationCap } from 'lucide-react';

const Faculty = () => {
  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="container mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="heading-hero mb-4">Our Expert Faculty</h1>
          <p className="body-large text-[var(--text-secondary)] max-w-2xl mx-auto">
            Learn from experienced educators led by an IIT-ian, dedicated to your academic success
          </p>
        </div>

        {/* Faculty Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {faculty.map((member, index) => (
            <div
              key={member.id}
              className="bg-[var(--bg-card)] rounded-xl shadow-sm overflow-hidden hover-lift"
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="heading-3 mb-1">{member.name}</h3>
                <p className="text-sm text-[var(--text-primary)] font-medium mb-3">
                  {member.designation}
                </p>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-2 text-sm text-[var(--text-secondary)]">
                    <BookOpen size={16} className="mt-1 flex-shrink-0" />
                    <span>{member.specialization}</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-[var(--text-secondary)]">
                    <Award size={16} className="mt-1 flex-shrink-0" />
                    <span>{member.experience}</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-[var(--text-secondary)]">
                    <GraduationCap size={16} className="mt-1 flex-shrink-0" />
                    <span>{member.education}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Teaching Approach */}
        <div className="bg-[var(--gradient-hero-subtle)] rounded-2xl p-8 md:p-12 mb-12">
          <h2 className="heading-1 mb-8 text-center">Our Teaching Approach</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen size={28} className="text-[var(--text-primary)]" />
              </div>
              <h3 className="heading-3 mb-3">Conceptual Clarity</h3>
              <p className="body-small text-[var(--text-secondary)]">
                We focus on building strong fundamental concepts rather than rote learning, 
                ensuring deep understanding of every topic
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Award size={28} className="text-[var(--text-primary)]" />
              </div>
              <h3 className="heading-3 mb-3">Personalized Mentoring</h3>
              <p className="body-small text-[var(--text-secondary)]">
                One-to-one attention with dedicated doubt-solving sessions to address 
                each student's unique learning needs
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <GraduationCap size={28} className="text-[var(--text-primary)]" />
              </div>
              <h3 className="heading-3 mb-3">Exam Excellence</h3>
              <p className="body-small text-[var(--text-secondary)]">
                Strategic preparation for board exams with regular mock tests, 
                time management techniques, and proven exam strategies
              </p>
            </div>
          </div>
        </div>

        {/* Qualifications */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-[var(--accent-purple-200)] rounded-xl p-8">
            <h3 className="heading-2 mb-4">Faculty Qualifications</h3>
            <ul className="space-y-3 body-small text-[var(--text-secondary)]">
              <li className="flex items-start gap-3">
                <span className="text-[var(--accent-purple-400)] mt-1">•</span>
                <span>Founder - IIT Patna alumnus and certified from Harvard University (USA)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[var(--accent-purple-400)] mt-1">•</span>
                <span>All faculty members hold a minimum of bachelor's degree in their relevant subjects</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[var(--accent-purple-400)] mt-1">•</span>
                <span>Specialized training in modern pedagogical techniques and student psychology</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[var(--accent-purple-400)] mt-1">•</span>
                <span>Regular training in latest CBSE/ICSE guidelines and examination patterns</span>
              </li>
            </ul>
          </div>

          <div className="bg-[var(--accent-blue-200)] rounded-xl p-8">
            <h3 className="heading-2 mb-4">Teaching Excellence</h3>
            <ul className="space-y-3 body-small text-[var(--text-secondary)]">
              <li className="flex items-start gap-3">
                <span className="text-[var(--accent-blue-400)] mt-1">•</span>
                <span>Average 6+ years of teaching experience per faculty member</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[var(--accent-blue-400)] mt-1">•</span>
                <span>Proven track record of producing board exam toppers</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[var(--accent-blue-400)] mt-1">•</span>
                <span>Subject matter experts in Mathematics, Science, Commerce and many more...</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[var(--accent-blue-400)] mt-1">•</span>
                <span>Passionate educators committed to student success and growth</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Faculty;
